// src/redux/admin/creditsApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logout } from "../slices/authSlice";

// ✅ Base Query with Token
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",

  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("x-auth-token", token); // ✅ backend expects this
    }
    return headers;
  },
});

// ✅ Auto Logout on 401 / 403
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    api.dispatch(logout());
  }

  return result;
};


// ✅ Credits API
export const creditsApi = createApi({
  reducerPath: "creditsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Credits", "Transactions", "Analytics", "Wallet", "AllocatableUsers"],
  endpoints: (builder) => ({
    // =========================
    // ✅ BASIC CREDIT APIs
    // =========================

    // ✅ GET /api/credits/my-wallet
    getMyWallet: builder.query({
      query: () => ({ url: "/credits/my-wallet" }),
      providesTags: ["Wallet"],
    }),

    // ✅ POST /api/credits/topup  (SUPER ADMIN)
    topupCredits: builder.mutation({
      query: (body) => ({
        url: "/credits/topup",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Wallet", "Transactions", "Analytics"],
    }),

    // ✅ POST /api/credits/allocate
    allocateCredits: builder.mutation({
      query: (body) => ({
        url: "/credits/allocate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Wallet", "Transactions", "Analytics"],
    }),

    // ✅ GET /api/credits/my-transactions
    getMyTransactions: builder.query({
      query: () => ({ url: "/credits/my-transactions" }),
      providesTags: ["Transactions"],
    }),

    // ✅ POST /api/credits/adjust
    adjustCredits: builder.mutation({
      query: (body) => ({
        url: "/credits/adjust",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Wallet", "Transactions", "Analytics"],
    }),

    // ✅ POST /api/credits/deduct
    forceDeductCredits: builder.mutation({
      query: (body) => ({
        url: "/credits/deduct",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Wallet", "Transactions", "Analytics"],
    }),

    // ✅ POST /api/credits/reverse/:txId
    reverseTransaction: builder.mutation({
      query: (txId) => ({
        url: `/credits/reverse/${txId}`,
        method: "POST",
      }),
      invalidatesTags: ["Wallet", "Transactions", "Analytics"],
    }),

    // ✅ GET /api/credits/transactions/:userId (ADMIN)
    getUserTransactions: builder.query({
      query: (userId) => ({ url: `/credits/transactions/${userId}` }),
      providesTags: ["Transactions"],
    }),

    // ✅ GET /api/credits/allocatable-users  (ROLE BASED)
    getAllocatableUsers: builder.query({
      query: () => ({ url: "/credits/allocatable-users" }),
      providesTags: ["AllocatableUsers"],
      transformResponse: (res) => res?.data || [],
    }),

    // =========================
    // ✅ ANALYTICS APIs
    // =========================

    // ✅ GET /api/credits/analytics/overview
    creditOverview: builder.query({
      query: () => ({ url: "/credits/analytics/overview" }),
      providesTags: ["Analytics"],
    }),

    // ✅ GET /api/credits/analytics/by-role
    creditByRole: builder.query({
      query: () => ({ url: "/credits/analytics/by-role" }),
      providesTags: ["Analytics"],
    }),

    // ✅ GET /api/credits/analytics/top-holders
    topCreditHolders: builder.query({
      query: (limit = 10) => ({
        url: `/credits/analytics/top-holders?limit=${limit}`,
      }),
      providesTags: ["Analytics"],
    }),

    // ✅ GET /api/credits/analytics/daily
    dailyCreditReport: builder.query({
      query: (days = 7) => ({
        url: `/credits/analytics/daily?days=${days}`,
      }),
      providesTags: ["Analytics"],
    }),

    // ✅ GET /api/credits/analytics/user/:userId
    userCreditSummary: builder.query({
      query: (userId) => ({
        url: `/credits/analytics/user/${userId}`,
      }),
      providesTags: ["Analytics"],
    }),

    // ✅ GET /api/credits/analytics/tree
    creditHierarchyTree: builder.query({
      query: () => ({ url: "/credits/analytics/tree" }),
      providesTags: ["Analytics"],
    }),
  }),
});

// ✅ EXPORT HOOKS (✅ FIXED)
export const {
  useGetMyWalletQuery,
  useTopupCreditsMutation,
  useAllocateCreditsMutation,
  useAdjustCreditsMutation,
  useForceDeductCreditsMutation,
  useReverseTransactionMutation,
  useGetMyTransactionsQuery,
  useGetUserTransactionsQuery,

  // ✅ NEW ✅
  useGetAllocatableUsersQuery,

  // ✅ Analytics
  useCreditOverviewQuery,
  useCreditByRoleQuery,
  useTopCreditHoldersQuery,
  useDailyCreditReportQuery,
  useUserCreditSummaryQuery,
  useCreditHierarchyTreeQuery,
} = creditsApi;
